"use client"

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react"

interface RestaurantUser {
  _id: string
  name: string
  email: string
  phone: string
  restaurant: {
    _id: string
    name: string
    logo?: string
    address: {
      street: string
      city: string
      state: string
      zipCode: string
    }
  }
  role: string
  permissions: {
    manageMenu: boolean
    manageOrders: boolean
    manageTables: boolean
    viewAnalytics: boolean
    manageStaff: boolean
    canProcessOrders?: boolean
    canManageInventory?: boolean
    canManageUsers?: boolean
    canViewReports?: boolean
    canManageTables?: boolean
    canManageMenu?: boolean
    canProcessRefunds?: boolean
    canViewAnalytics?: boolean
  }
  isOwner?: boolean
  type?: string
}

interface RestaurantAuthContextType {
  user: RestaurantUser | null
  token: string | null
  login: (token: string, user: RestaurantUser) => void
  logout: () => void
  isAuthenticated: boolean
  loading: boolean
  isOwner: boolean
  hasPermission: (permission: string) => boolean
}

const RestaurantAuthContext = createContext<RestaurantAuthContextType | undefined>(undefined)

export const useRestaurantAuth = () => {
  const context = useContext(RestaurantAuthContext)
  if (context === undefined) {
    throw new Error("useRestaurantAuth must be used within a RestaurantAuthProvider")
  }
  return context
}

interface RestaurantAuthProviderProps {
  children: ReactNode
}

export const RestaurantAuthProvider: React.FC<RestaurantAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<RestaurantUser | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for existing restaurant authentication on mount
    const restaurantToken = localStorage.getItem("restaurantToken")
    const restaurantUser = localStorage.getItem("restaurantUser")

    if (restaurantToken && restaurantUser) {
      try {
        const parsedUser = JSON.parse(restaurantUser)
        setToken(restaurantToken)
        setUser(parsedUser)
      } catch (error) {
        console.error("Error parsing restaurant user data:", error)
        // Clear invalid data
        localStorage.removeItem("restaurantToken")
        localStorage.removeItem("restaurantUser")
      }
    }
    setLoading(false)
  }, [])

  const login = (newToken: string, newUser: RestaurantUser) => {
    setToken(newToken)
    setUser(newUser)
    localStorage.setItem("restaurantToken", newToken)
    localStorage.setItem("restaurantUser", JSON.stringify(newUser))
  }

  const logout = () => {
    setToken(null)
    setUser(null)
    localStorage.removeItem("restaurantToken")
    localStorage.removeItem("restaurantUser")
    // Redirect to restaurant login
    window.location.href = "/restaurant/login"
  }

  // Check if user is restaurant owner
  const isOwner = user?.role === "restaurant_owner" || user?.isOwner === true

  // Check if user has a specific permission
  const hasPermission = (permission: string): boolean => {
    if (isOwner) {
      return true // Owners have all permissions
    }
    
    if (!user?.permissions) {
      return false
    }

    // Check both old and new permission formats
    const permissions = user.permissions as any
    return permissions[permission] === true || 
           permissions[permission.toLowerCase()] === true ||
           permissions[permission.replace('can', '').replace(/([A-Z])/g, (m: string) => m.toLowerCase())] === true
  }

  const value: RestaurantAuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!token && !!user,
    loading,
    isOwner,
    hasPermission,
  }

  return (
    <RestaurantAuthContext.Provider value={value}>
      {children}
    </RestaurantAuthContext.Provider>
  )
} 